/**
 * Données structurées schema.org (JSON-LD). Chaque fonction renvoie un nœud du graphe de la page ; ils se relient par leur @id.
 * Rien n'est déclaré qui ne soit visible sur la page : pas de note moyenne, pas de tarif.
 */
import site from '@/content/site';
import {plain} from '@/seo/inline';

const ids = {
    organization: `${site.url}/#organization`,
    website: `${site.url}/#website`,
    person: `${site.url}/#franck-besson`,
    software: `${site.url}/#gestionnaire-citron`
};

export function absoluteUrl(path) {
    return /^https?:\/\//.test(path) ? path : site.url + path;
}

export function organization() {
    return {
        '@type': 'Organization',
        '@id': ids.organization,
        name: site.name,
        url: `${site.url}/`,
        logo: {'@type': 'ImageObject', url: absoluteUrl(site.logo)},
        email: site.email,
        telephone: site.phoneIntl,
        foundingDate: site.foundingDate,
        founder: {'@id': ids.person},
        areaServed: {'@type': 'Country', name: 'France'},
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: site.email,
            telephone: site.phoneIntl,
            availableLanguage: 'French'
        }
    };
}

export function person() {
    return {
        '@type': 'Person',
        '@id': ids.person,
        name: site.author.name,
        jobTitle: site.author.jobTitle,
        url: absoluteUrl(site.author.path),
        sameAs: [site.linkedin],
        worksFor: {'@id': ids.organization}
    };
}

export function website() {
    return {
        '@type': 'WebSite',
        '@id': ids.website,
        url: `${site.url}/`,
        name: site.name,
        inLanguage: 'fr-FR',
        publisher: {'@id': ids.organization}
    };
}

export function software() {
    return {
        '@type': 'SoftwareApplication',
        '@id': ids.software,
        name: site.product,
        url: absoluteUrl('/gestionnaire-citron'),
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Logiciel de devis et de facturation',
        operatingSystem: 'Web',
        inLanguage: 'fr-FR',
        description: 'Application web de gestion pour les TPE et les indépendants, entre CRM et ERP léger : clients et prospects, devis, factures, acomptes, avoirs, facturation électronique Factur-X, encaissements, achats et stock, export comptable FEC et statistiques.',
        featureList: [
            'Devis avec signature par QR code',
            'Factures d\'acompte, factures finales et avoirs',
            'Factures au format Factur-X transmises via une plateforme agréée',
            'Suivi des encaissements, de la TVA et des impayés',
            'CRM : fiches clients et prospects, historique des échanges',
            'Achats, fournisseurs, réception, stock et marge par dossier (ERP léger)',
            'Export comptable FEC et classeur Excel avec contrôles et lettrage',
            'Statistiques : chiffre d\'affaires, devis, clients et encaissements',
            'Accès comptable limité aux données utiles',
            'Espace partagé avec droits selon le rôle'
        ],
        publisher: {'@id': ids.organization}
    };
}

export function webPage(path, name, description, extra = {}) {
    const url = absoluteUrl(path);
    return {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name,
        description,
        inLanguage: 'fr-FR',
        isPartOf: {'@id': ids.website},
        ...extra
    };
}

export function breadcrumb(items) {
    return {
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: absoluteUrl(item.path)
        }))
    };
}

export function faqPage(items) {
    return {
        '@type': 'FAQPage',
        mainEntity: items.map(item => ({
            '@type': 'Question',
            name: plain(item.q),
            acceptedAnswer: {'@type': 'Answer', text: plain(item.a)}
        }))
    };
}

export function blogPosting(article, imagePath) {
    const url = absoluteUrl(article.path);
    return {
        '@type': 'BlogPosting',
        '@id': `${url}#article`,
        headline: article.h1,
        description: article.description,
        url,
        mainEntityOfPage: {'@id': `${url}#webpage`},
        image: absoluteUrl(imagePath),
        datePublished: article.published,
        dateModified: article.updated || article.published,
        inLanguage: 'fr-FR',
        wordCount: article.wordCount,
        articleSection: article.category,
        author: {'@id': ids.person},
        publisher: {'@id': ids.organization}
    };
}

export function service(name, description, path, serviceType) {
    return {
        '@type': 'Service',
        name,
        description,
        serviceType,
        url: absoluteUrl(path),
        provider: {'@id': ids.organization},
        areaServed: [{'@type': 'City', name: site.area}, {'@type': 'Country', name: 'France'}]
    };
}

export {ids};
